import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  Row,
  Col,
  Table,
  Badge,
  Spinner,
  Collapse,
  Input,
} from 'reactstrap';
import { displayValue as displayParcelValue } from '../../../../../../../../Components/QuantityTable/utils/quantityDeliveryUtils';
import { getAuthToken } from '../../../../../../../../auth/tokenvalidation';
import { api } from '../../../../../../../../config';
import useSubmittedBidder from '../../../../../../hooks/useSubmittedBidder';
import {
  buildRequirementsFromParcel,
  collectImprovementSelections,
  deliveryDetailRowMatchesBidder,
  displayValue,
  filterImprovementOfferRows,
  flattenEvaluationDeliveryDetail,
  flattenSubmittedBidderDeliveryDetails,
  getRequirementIncotermRouteLabel,
  isOfferRowImprovement,
  getOfferRowImprovementStatusLabel,
  mergeDeliveryDetailPricingIntoRows,
  offerRowMatchesRequirement,
  isHpsAvailable,
} from '../../../../../../utils/tenderEvaluationHelpers';
import {
  getProcurementProductNameId,
  requiresLpgCargoRatio,
  usesDyedUndyedAlpha,
} from '../../../../../../../../shared/offer-domain/constants';
import { formatAlphaImprovementDisplay } from '../../../../../../../../shared/offer-domain/improvement';
import AlphaImprovementEditableCells from '../shared/AlphaImprovementEditableCells';
import {
  getHpsAlphaValues,
  resolveMatchingHpsKey,
  MatchingHpsBadge,
  HpsAlphaCell,
} from '../shared/hpsMatching';
import {
  formatAlphaPrice,
  formatIncotermRoute,
  formatParcelSize,
  formatPortDateRange,
  formatPortLocation,
  isAlphaFilled,
  coalesceAlphaPrice,
} from '../../../../../../utils/deliveryDetailUtils';
import {
  enrichLpgDeliveryItemsWithCargoRatio,
  enrichDeliveryItemsWithCounterOffers,
  enrichDeliveryItemsWithFobLocations,
  isCounterDateChanged,
  isCounterValueChanged,
  FobLocationLabel,
} from '../../../../../../../../shared/offer-domain/delivery';
import { CARGO_RATIO_LABELS } from '../../../../../../../TenderOpening/components/Detail/HpsParcel/lpg/utils';
import {
  COUNTER_ACCENT,
  COUNTER_HIGHLIGHT_BG,
} from '../../../../../../../../shared/offer-domain/constants';
import ModalOffer from '../../ModalOffer';

const LPG_UNIT = 'MT';
const ALPHA_UNIT = 'USD/MT';

const counterValueStyle = {
  backgroundColor: COUNTER_HIGHLIGHT_BG,
  color: COUNTER_ACCENT,
  fontWeight: 600,
};

/** Unique incoterms only — LPG delivery-detail can expand parcel/ratio rows. */
const buildLpgRequirementsFromParcel = parcel => {
  const rows = buildRequirementsFromParcel(parcel);
  const seen = new Map();

  rows.forEach(row => {
    const incotermId = row?.quantityIncotermId;
    const key = incotermId
      ? `id:${incotermId}`
      : `route:${row?.incoterm ?? ''}|${row?.route ?? ''}`;
    if (seen.has(key)) return;
    seen.set(key, row);
  });

  return [...seen.values()].map((row, index) => ({
    ...row,
    requirementIndex: index,
  }));
};

const PortSummary = ({
  label,
  port,
  parcelSizes = [],
  dateRange,
  showParcelSize = true,
}) => {
  const sizes = (
    Array.isArray(parcelSizes) ? parcelSizes : [parcelSizes]
  ).filter(Boolean);

  return (
    <Col md={3}>
      <div className='text-muted fw-semibold fs-12 mb-2'>{label}</div>
      <div className='fs-13'>
        <span className='text-muted d-block fs-12'>Port :</span>
        <span className='text-dark d-block mb-2'>{displayValue(port)}</span>
        {showParcelSize && (
          <>
            <span className='text-muted d-block fs-12'>Parcel Size :</span>
            <span className='text-dark d-block mb-2'>
              {sizes.length ? sizes.join(', ') : '-'}
            </span>
          </>
        )}
        {dateRange && (
          <>
            <span className='text-muted d-block fs-12'>ALD/ADD :</span>
            <span className='text-dark d-block'>{dateRange}</span>
          </>
        )}
      </div>
    </Col>
  );
};

const formatTimelineRange = timeline => {
  if (!timeline) return null;
  const start = timeline.startDate || timeline.startDate2;
  const end = timeline.endDate || timeline.endDate2;
  if (!start && !end) return null;
  if (start && end) return `${start} - ${end}`;
  return start || end;
};

const getProductLabel = procurement => {
  const productName =
    procurement?.productType?.product?.name ??
    procurement?.productType?.Product?.Name;
  const productDesc =
    procurement?.productType?.description ??
    procurement?.productType?.Description;

  if (productName && productDesc) return `${productName} - ${productDesc}`;
  return productName || productDesc || '-';
};

const getQuantityLabel = parcel => {
  const quantities =
    parcel?.quantities ?? parcel?.listQuantityInformation ?? [];
  if (!quantities.length) return '-';
  return quantities
    .map(item => `${displayParcelValue(item.quantity)} ${LPG_UNIT}`)
    .join(', ');
};

const resolveCargoRatioLabel = row => {
  if (row?.cargoRatioLabel) return row.cargoRatioLabel;
  if (row?.ratioLpg && typeof row.ratioLpg === 'string') return row.ratioLpg;
  if (row?.ratioLpg?.ratioName) return row.ratioLpg.ratioName;
  if (row?.ratioLpg?.name) return row.ratioLpg.name;

  const ratioId = Number(row?.cargoRatio);
  if (Number.isFinite(ratioId) && ratioId >= 1) {
    return CARGO_RATIO_LABELS[ratioId - 1] ?? String(row.cargoRatio);
  }

  return null;
};

const resolveParcelLabel = row => {
  if (row?.parcelSizeLabel) return row.parcelSizeLabel;
  return formatParcelSize(row?.parcelSize ?? row?.deliveryBasis, LPG_UNIT);
};

const collectParcelSizeLabels = (parcel, rows = []) => {
  const seen = new Set();
  const labels = [];

  const pushLabel = label => {
    if (!label || label.startsWith('-') || seen.has(label)) return;
    seen.add(label);
    labels.push(label);
  };

  rows.forEach(row => {
    pushLabel(resolveParcelLabel(row));
  });
  if (labels.length) return labels;

  const quantities =
    parcel?.quantities ?? parcel?.listQuantityInformation ?? [];
  quantities.forEach(item => {
    pushLabel(formatParcelSize(item?.quantity, LPG_UNIT));
  });
  if (labels.length) return labels;

  const fromDelivery = rows.find(
    row => Array.isArray(row?.parcelSizeLabels) && row.parcelSizeLabels.length
  )?.parcelSizeLabels;
  (fromDelivery || []).forEach(pushLabel);

  return labels;
};

const renderLpgPortBlock = (
  port,
  parcelSizes = [],
  {
    startDate,
    endDate,
    fallbackDate,
    highlight = {},
    showParcelSize = true,
    locationNames = null,
    route = null,
  } = {}
) => {
  const dateText = formatPortDateRange(startDate, endDate, fallbackDate);
  const sizes = (parcelSizes || []).filter(size => {
    const text = String(size ?? '').trim();
    return text && text !== '-' && !text.startsWith('-');
  });
  const highlightPort = highlight === true || highlight?.port;
  const highlightParcel = highlight === true || highlight?.parcelSize;
  const highlightDate = highlight === true || highlight?.date;
  const hasLocationNames =
    Array.isArray(locationNames) && locationNames.length > 0;

  return (
    <div className='fs-12 lh-base d-flex flex-column gap-1'>
      <span>
        <span className='text-muted'>Port : </span>
        <span
          className={highlightPort ? 'fw-semibold' : 'text-dark'}
          style={
            highlightPort
              ? {
                  ...counterValueStyle,
                  padding: '0 4px',
                  borderRadius: 2,
                  display: 'inline-block',
                }
              : undefined
          }
        >
          {hasLocationNames ? (
            <FobLocationLabel
              names={locationNames}
              fallback={port}
              route={route}
            />
          ) : (
            formatPortLocation(port)
          )}
        </span>
      </span>
      {showParcelSize && (
        <span>
          <span className='text-muted'>Parcel Size : </span>
          <span
            className={highlightParcel ? 'fw-semibold' : 'text-dark'}
            style={
              highlightParcel
                ? {
                    ...counterValueStyle,
                    padding: '0 4px',
                    borderRadius: 2,
                    display: 'inline-block',
                  }
                : undefined
            }
          >
            {sizes.length ? sizes.join(', ') : '-'}
          </span>
        </span>
      )}
      <span>
        <span className='text-muted'>ALD/ADD : </span>
        <span
          className={highlightDate ? 'fw-semibold' : 'text-dark'}
          style={
            highlightDate
              ? {
                  ...counterValueStyle,
                  padding: '0 4px',
                  borderRadius: 2,
                  display: 'inline-block',
                }
              : undefined
          }
        >
          {dateText}
        </span>
      </span>
    </div>
  );
};

const getBidderRequirementRows = ({
  bidder,
  requirement,
  parcelId,
  deliveryDetails,
}) =>
  (deliveryDetails ?? []).filter(
    row =>
      deliveryDetailRowMatchesBidder(row, bidder) &&
      offerRowMatchesRequirement(row, requirement, parcelId)
  );

const getOfferRowKey = (parcelId, requirementIndex, bidder, detailRow, kind) =>
  `${parcelId ?? 'parcel'}-${requirementIndex}-${bidder?.id ?? bidder?.idOffer}-${detailRow?.quantityInformationDetailId ?? detailRow?.id ?? 'x'}-${detailRow?.cargoRatio ?? ''}-${resolveParcelLabel(detailRow)}-${kind}`;

const getOfferSelectionKey = (parcelId, requirementIndex, bidder, kind) =>
  `${parcelId ?? 'parcel'}-${requirementIndex}-${bidder?.id ?? bidder?.idOffer}-${kind}`;

const getCompliantLpgRows = ({
  bidders,
  requirement,
  requirementIndex,
  parcelId,
  deliveryDetails,
}) => {
  if (!bidders?.length) return [];

  return bidders.flatMap(bidder => {
    const matchedRows = getBidderRequirementRows({
      bidder,
      requirement,
      parcelId,
      deliveryDetails,
    });

    const offerRows = matchedRows
      .filter(row => isAlphaFilled(row?.alpha))
      .map(detailRow => ({
        bidder,
        detailRow,
        kind: 'offer',
        selectionKey: getOfferSelectionKey(
          parcelId,
          requirementIndex,
          bidder,
          'offer'
        ),
        rowKey: getOfferRowKey(
          parcelId,
          requirementIndex,
          bidder,
          detailRow,
          'offer'
        ),
      }));

    const counterCandidates = matchedRows.filter(row =>
      isAlphaFilled(row?.counterAlpha)
    );

    /** Counter is defined per parcel size; original ratio rows must not duplicate it. */
    const counterParcelByLabel = new Map();
    counterCandidates.forEach(row => {
      const label = String(resolveParcelLabel(row) ?? '');
      if (!label) return;
      const current = counterParcelByLabel.get(label);
      if (
        !current ||
        (!current.quantityInformationDetailCounterId &&
          row.quantityInformationDetailCounterId)
      ) {
        counterParcelByLabel.set(label, row);
      }
    });

    const counterRatios = counterCandidates.find(
      row =>
        Array.isArray(row?.counterCargoRatios) && row.counterCargoRatios.length
    )?.counterCargoRatios ?? [{ id: null, label: null }];

    const counterRows = [];
    counterRatios.forEach(ratio => {
      counterParcelByLabel.forEach(detailRow => {
        counterRows.push({
          bidder,
          detailRow,
          counterRatio: ratio,
          kind: 'counter',
          selectionKey: getOfferSelectionKey(
            parcelId,
            requirementIndex,
            bidder,
            'counter'
          ),
          rowKey: `${getOfferRowKey(
            parcelId,
            requirementIndex,
            bidder,
            detailRow,
            'counter'
          )}-${ratio?.id ?? 'none'}`,
        });
      });
    });

    return [...offerRows, ...counterRows];
  });
};

const getRowCargoRatioLabel = row =>
  row?.kind === 'counter'
    ? (row?.counterRatio?.label ?? null)
    : resolveCargoRatioLabel(row?.detailRow);

const getCargoRatioSortKey = row => String(getRowCargoRatioLabel(row) ?? '-');

const getParcelSizeSortValue = detailRow => {
  const raw = detailRow?.parcelSize ?? detailRow?.deliveryBasis;
  const numeric = Number(String(raw ?? '').replace(/[^\d.-]/g, ''));
  return Number.isFinite(numeric) ? numeric : 0;
};

const buildGroupedOfferRows = (compliantRows = []) => {
  const groups = [];
  const indexByKey = new Map();

  compliantRows.forEach(row => {
    const groupKey =
      row.selectionKey ??
      `${row.bidder?.id ?? row.bidder?.idOffer}-${row.kind}`;
    if (!indexByKey.has(groupKey)) {
      indexByKey.set(groupKey, groups.length);
      groups.push({
        groupKey,
        bidder: row.bidder,
        kind: row.kind,
        rows: [],
      });
    }
    groups[indexByKey.get(groupKey)].rows.push(row);
  });

  return groups.flatMap(group => {
    const sorted = [...group.rows].sort((a, b) => {
      const ratioCmp = getCargoRatioSortKey(a).localeCompare(
        getCargoRatioSortKey(b)
      );
      if (ratioCmp !== 0) return ratioCmp;
      return (
        getParcelSizeSortValue(a.detailRow) -
        getParcelSizeSortValue(b.detailRow)
      );
    });

    const ratioBuckets = new Map();
    sorted.forEach(row => {
      const key = getCargoRatioSortKey(row);
      if (!ratioBuckets.has(key)) ratioBuckets.set(key, []);
      ratioBuckets.get(key).push(row);
    });

    const result = [];
    let offset = 0;
    const groupRowSpan = sorted.length;

    ratioBuckets.forEach((ratioRows, cargoRatioKey) => {
      ratioRows.forEach((row, sizeIndex) => {
        result.push({
          ...row,
          showGroupCells: offset === 0,
          groupRowSpan,
          showCargoRatioCell: sizeIndex === 0,
          cargoRatioRowSpan: ratioRows.length,
          cargoRatioLabel:
            cargoRatioKey === '-'
              ? null
              : (getRowCargoRatioLabel(row) ?? cargoRatioKey),
          selectionKey: group.groupKey,
        });
        offset += 1;
      });
    });

    return result;
  });
};

const requirementHasOfferData = ({
  requirement,
  requirementIndex,
  parcelId,
  deliveryDetails,
  bidders,
  improvementOnly = false,
}) => {
  const rows = getCompliantLpgRows({
    bidders,
    requirement,
    requirementIndex,
    parcelId,
    deliveryDetails,
  });
  return (improvementOnly ? filterImprovementOfferRows(rows) : rows).length > 0;
};

const OfferDetailRows = ({
  compliantRows,
  isLoading,
  selectedRowKeys,
  onSelectRow,
  requiresCargoRatio,
  operationalTolerance,
  parcelSizes = [],
  onViewDetail,
  improvementOnly = false,
  usesDyedUndyed = false,
  colSpan,
  alphaImprovementEditable = false,
  onAlphaImprovementChange,
  hpsAvailable = false,
}) => {
  const groupedRows = useMemo(
    () => buildGroupedOfferRows(compliantRows),
    [compliantRows]
  );

  if (isLoading) {
    return (
      <tr>
        <td colSpan={colSpan} className='text-center py-4'>
          <Spinner size='sm' color='primary' />
          <span className='text-muted ms-2 fs-13'>Loading offer detail...</span>
        </td>
      </tr>
    );
  }

  if (!groupedRows.length) {
    return (
      <tr>
        <td colSpan={colSpan} className='text-center text-muted py-4 fs-13'>
          {improvementOnly
            ? 'Belum ada data improvement.'
            : 'Belum ada data offer dengan alpha terisi.'}
        </td>
      </tr>
    );
  }

  return groupedRows.map(
    (
      {
        bidder,
        detailRow,
        kind,
        rowKey,
        selectionKey: rowSelectionKey,
        showGroupCells,
        groupRowSpan,
        showCargoRatioCell,
        cargoRatioRowSpan,
        cargoRatioLabel: groupedCargoRatioLabel,
        counterRatio = null,
      },
      index
    ) => {
      const isCounter = kind === 'counter';
      const isPerCargoRatioImprovement =
        improvementOnly &&
        requiresCargoRatio &&
        (isCounter
          ? !!counterRatio?.recordId
          : !!detailRow?.quantityInformationCargoRatioId);
      const improvementDetailRow = counterRatio
        ? {
            ...detailRow,
            counterCargoRatioRecordId:
              counterRatio.recordId ??
              detailRow.counterCargoRatioRecordId ??
              null,
            counterCargoRatioId:
              counterRatio.id ?? detailRow.counterCargoRatioId,
            quantityInformationCargoRatioId:
              counterRatio.quantityInformationCargoRatioFK ??
              detailRow.quantityInformationCargoRatioId,
            cargoRatioParcelSize:
              counterRatio.parcelSize ?? detailRow.cargoRatioParcelSize,
            cargoRatioAlphaImprovement:
              counterRatio.alphaImprovement ??
              counterRatio.cargoRatioAlphaImprovement ??
              detailRow.cargoRatioAlphaImprovement ??
              null,
            improvementAlpha:
              counterRatio.alphaImprovement ??
              detailRow.improvementAlpha ??
              null,
            alphaHps: counterRatio.alphaHps ?? detailRow.alphaHps,
            alphaDyedHps: counterRatio.alphaDyedHps ?? detailRow.alphaDyedHps,
            alphaUndyedHps:
              counterRatio.alphaUndyedHps ?? detailRow.alphaUndyedHps,
            cargoRatioSnapshot:
              counterRatio.cargoRatioSnapshot ??
              detailRow.cargoRatioSnapshot ??
              null,
            counterCargoRatioSnapshot:
              counterRatio.cargoRatioSnapshot ??
              detailRow.counterCargoRatioSnapshot ??
              null,
          }
        : detailRow;
      const alphaValue = isCounter
        ? coalesceAlphaPrice(
            detailRow.counterAlpha,
            detailRow.alpha2,
            detailRow.alphaMitra2,
            detailRow.alpha,
            detailRow.alphaMitra1,
            detailRow.AlphaMitra1
          )
        : coalesceAlphaPrice(
            detailRow.alpha,
            detailRow.alphaMitra1,
            detailRow.AlphaMitra1,
            detailRow.alpha1
          );
      if (!isAlphaFilled(alphaValue)) return null;

      const parcelLabel = resolveParcelLabel(detailRow);
      const cargoRatioLabel = isCounter
        ? groupedCargoRatioLabel
        : (groupedCargoRatioLabel ?? resolveCargoRatioLabel(detailRow));
      const incoterm = isCounter
        ? (detailRow.counterIncoterm ?? detailRow.incoterm)
        : detailRow.incoterm;
      const route = isCounter
        ? (detailRow.counterRoute ?? detailRow.route)
        : detailRow.route;
      const originalIncotermRoute = formatIncotermRoute(
        detailRow.incoterm,
        detailRow.route
      );
      const counterIncotermRoute = formatIncotermRoute(incoterm, route);
      const incotermRouteChanged =
        isCounter &&
        !!detailRow.counterIncoterm &&
        isCounterValueChanged(originalIncotermRoute, counterIncotermRoute);
      const originalPort1 = detailRow.port1 || detailRow.loadingPort;
      const counterPort1 =
        detailRow.counterLoadingPort ||
        detailRow.port1 ||
        detailRow.loadingPort;
      const port1 = isCounter ? counterPort1 : originalPort1;
      const port2 = isCounter
        ? detailRow.counterPort2 || detailRow.port2
        : detailRow.port2;
      const port3 = isCounter
        ? detailRow.counterPort3 || detailRow.port3
        : detailRow.port3;
      const alphaChanged =
        isCounter &&
        isCounterValueChanged(detailRow.alpha, detailRow.counterAlpha);
      const port1Highlight = isCounter
        ? {
            port: isCounterValueChanged(
              originalPort1,
              detailRow.counterLoadingPort
            ),
            date: isCounterDateChanged(
              detailRow.port1StartDate,
              detailRow.port1EndDate,
              detailRow.counterStartDate,
              detailRow.counterEndDate,
              detailRow.addAld,
              detailRow.counterAddAld
            ),
          }
        : {};
      const port2Highlight = isCounter
        ? {
            port: isCounterValueChanged(
              detailRow.port2,
              detailRow.counterPort2
            ),
            date: isCounterDateChanged(
              detailRow.port2StartDate,
              detailRow.port2EndDate,
              detailRow.counterStartDate2,
              detailRow.counterEndDate2,
              detailRow.addAld2,
              detailRow.counterAddAld2
            ),
          }
        : {};
      const port3Highlight = isCounter
        ? {
            port: isCounterValueChanged(
              detailRow.port3,
              detailRow.counterPort3
            ),
            date: isCounterDateChanged(
              detailRow.port3StartDate,
              detailRow.port3EndDate,
              detailRow.counterStartDate3,
              detailRow.counterEndDate3,
              detailRow.addAld3,
              detailRow.counterAddAld3
            ),
          }
        : {};
      const groupSelected =
        !!rowSelectionKey && selectedRowKeys.has(rowSelectionKey);
      const isImprovement = isOfferRowImprovement({ bidder, detailRow, kind });

      return (
        <tr
          key={rowKey ?? `${bidder.id ?? index}-${index}`}
          className='border-bottom'
        >
          {showGroupCells && (
            <>
              {!improvementOnly && (
                <td rowSpan={groupRowSpan} className='text-center align-top'>
                  <Input
                    type='checkbox'
                    className='form-check-input'
                    checked={groupSelected}
                    disabled={isImprovement}
                    onChange={e => {
                      if (isImprovement) return;
                      const checked = e.target.checked;
                      onSelectRow?.(rowSelectionKey, checked);
                    }}
                    aria-label={`Pilih ${displayValue(bidder.idOffer)}`}
                  />
                </td>
              )}
              {!improvementOnly && (
                <td
                  rowSpan={groupRowSpan}
                  className='text-dark align-top fs-12'
                >
                  {getOfferRowImprovementStatusLabel({
                    bidder,
                    detailRow,
                    kind,
                  }) === 'Requested' ? (
                    <Badge
                      color='success'
                      className='px-2 py-1 fs-11 fw-medium'
                    >
                      Requested
                    </Badge>
                  ) : (
                    <Badge
                      color='secondary'
                      className='px-2 py-1 fs-11 fw-medium'
                    >
                      Not Requested
                    </Badge>
                  )}
                </td>
              )}
              <td rowSpan={groupRowSpan} className='text-dark align-top'>
                <div>{displayValue(bidder.idOffer)}</div>
                <div className='text-muted fs-12'>
                  {displayValue(bidder.companyIdentityMask)}
                </div>
              </td>
              <td rowSpan={groupRowSpan} className='align-top'>
                {isCounter ? (
                  <span
                    className='badge px-2 py-1 fs-11'
                    style={{
                      backgroundColor: COUNTER_ACCENT,
                      color: '#fff',
                    }}
                  >
                    Counter
                  </span>
                ) : (
                  <Badge color='primary' className='px-2 py-1 fs-11'>
                    Compliant
                  </Badge>
                )}
              </td>
              <td
                rowSpan={groupRowSpan}
                className='text-dark align-top'
                style={incotermRouteChanged ? counterValueStyle : undefined}
              >
                {formatIncotermRoute(incoterm, route)}
              </td>
              <td rowSpan={groupRowSpan} className='text-dark align-top'>
                {isCounter
                  ? operationalTolerance != null && operationalTolerance !== ''
                    ? `${operationalTolerance}%`
                    : '-'
                  : ''}
              </td>
              <td rowSpan={groupRowSpan} className='align-top'>
                {isCounter
                  ? renderLpgPortBlock(port1, parcelSizes, {
                      startDate:
                        detailRow.counterStartDate ?? detailRow.port1StartDate,
                      endDate:
                        detailRow.counterEndDate ?? detailRow.port1EndDate,
                      fallbackDate: detailRow.counterAddAld ?? detailRow.addAld,
                      highlight: port1Highlight,
                      locationNames: detailRow.fobLocationNames,
                      route: detailRow.counterRoute ?? detailRow.route,
                    })
                  : '-'}
              </td>
              <td rowSpan={groupRowSpan} className='align-top'>
                {isCounter && port2
                  ? renderLpgPortBlock(port2, [], {
                      startDate:
                        detailRow.counterStartDate2 ?? detailRow.port2StartDate,
                      endDate:
                        detailRow.counterEndDate2 ?? detailRow.port2EndDate,
                      fallbackDate:
                        detailRow.counterAddAld2 ?? detailRow.addAld2,
                      highlight: port2Highlight,
                      showParcelSize: false,
                    })
                  : '-'}
              </td>
              <td rowSpan={groupRowSpan} className='align-top'>
                {isCounter && port3
                  ? renderLpgPortBlock(port3, [], {
                      startDate:
                        detailRow.counterStartDate3 ?? detailRow.port3StartDate,
                      endDate:
                        detailRow.counterEndDate3 ?? detailRow.port3EndDate,
                      fallbackDate:
                        detailRow.counterAddAld3 ?? detailRow.addAld3,
                      highlight: port3Highlight,
                      showParcelSize: false,
                    })
                  : '-'}
              </td>
            </>
          )}
          {requiresCargoRatio && showCargoRatioCell && (
            <td rowSpan={cargoRatioRowSpan} className='text-dark align-top'>
              {cargoRatioLabel ? (
                <span className='badge bg-primary-subtle text-primary border border-primary-subtle fw-semibold rounded-3 fs-12'>
                  {cargoRatioLabel}
                </span>
              ) : (
                '-'
              )}
            </td>
          )}
          <td className='text-dark fw-semibold'>{parcelLabel}</td>
          <td
            className='text-dark'
            style={alphaChanged ? counterValueStyle : undefined}
          >
            {formatAlphaPrice(alphaValue, ALPHA_UNIT)}
          </td>
          {!improvementOnly && (
            <>
              <td className='text-dark'>
                <HpsAlphaCell
                  usesDyedUndyed={false}
                  hps={getHpsAlphaValues(detailRow)}
                  suffix={ALPHA_UNIT}
                  hpsAvailable={hpsAvailable}
                />
              </td>
              <td className='text-nowrap'>
                <MatchingHpsBadge
                  matchKey={resolveMatchingHpsKey({
                    usesDyedUndyed: false,
                    offerAlpha: alphaValue,
                    offerDyed: null,
                    offerUndyed: null,
                    hps: getHpsAlphaValues(detailRow),
                    statusFromApi:
                      detailRow.matchingHps ??
                      detailRow.hpsMatching ??
                      detailRow.matchingHpsStatus,
                    hpsAvailable,
                  })}
                />
              </td>
            </>
          )}
          {improvementOnly
            ? (isPerCargoRatioImprovement || showGroupCells) &&
              (alphaImprovementEditable ? (
                <AlphaImprovementEditableCells
                  detailRow={improvementDetailRow}
                  kind={kind}
                  usesDyedUndyed={usesDyedUndyed}
                  pricingUnit={ALPHA_UNIT}
                  onAlphaImprovementChange={onAlphaImprovementChange}
                  rowSpan={
                    isPerCargoRatioImprovement ? undefined : groupRowSpan
                  }
                />
              ) : usesDyedUndyed ? (
                <>
                  <td
                    rowSpan={
                      isPerCargoRatioImprovement ? undefined : groupRowSpan
                    }
                    className='text-dark align-top'
                  >
                    {formatAlphaImprovementDisplay(
                      improvementDetailRow,
                      kind,
                      'dyed',
                      ALPHA_UNIT
                    )}
                  </td>
                  <td
                    rowSpan={
                      isPerCargoRatioImprovement ? undefined : groupRowSpan
                    }
                    className='text-dark align-top'
                  >
                    {formatAlphaImprovementDisplay(
                      improvementDetailRow,
                      kind,
                      'undyed',
                      ALPHA_UNIT
                    )}
                  </td>
                </>
              ) : (
                <td
                  rowSpan={
                    isPerCargoRatioImprovement ? undefined : groupRowSpan
                  }
                  className='text-dark align-top'
                >
                  {formatAlphaImprovementDisplay(
                    improvementDetailRow,
                    kind,
                    'alpha',
                    ALPHA_UNIT
                  )}
                </td>
              ))
            : showGroupCells && (
                <td rowSpan={groupRowSpan} className='text-center align-top'>
                  <button
                    type='button'
                    className='btn btn-soft-primary btn-sm btn-icon'
                    onClick={() => onViewDetail?.(bidder, detailRow)}
                    title='Detail'
                  >
                    <i className='ri-eye-line align-bottom'></i>
                  </button>
                </td>
              )}
        </tr>
      );
    }
  );
};

const RequirementCard = ({
  requirement,
  requirementIndex,
  parcelId,
  parcel,
  bidders,
  isLoadingBidders,
  deliveryDetails,
  isLoadingDeliveryDetails,
  procurement,
  requiresCargoRatio,
  onViewDetail,
  improvementOnly = false,
  selectionKey,
  onRequirementSelectionChange,
  selectionClearToken = 0,
  alphaImprovementEditable = false,
  onAlphaImprovementChange,
}) => {
  const hasData = useMemo(
    () =>
      requirementHasOfferData({
        requirement,
        requirementIndex,
        parcelId,
        deliveryDetails,
        bidders,
        improvementOnly,
      }),
    [
      requirement,
      requirementIndex,
      parcelId,
      deliveryDetails,
      bidders,
      improvementOnly,
    ]
  );

  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (hasData) setIsExpanded(true);
  }, [hasData]);

  const timeline = requirement?.timeline;
  const incotermRouteLabel = getRequirementIncotermRouteLabel(requirement);
  const selectAllRef = useRef(null);
  const operationalTolerance = procurement?.operationalTolerance;
  const usesDyedUndyed = usesDyedUndyedAlpha(
    getProcurementProductNameId(procurement)
  );
  const colSpan = improvementOnly
    ? requiresCargoRatio
      ? usesDyedUndyed
        ? 12
        : 11
      : usesDyedUndyed
        ? 11
        : 10
    : requiresCargoRatio
      ? 15
      : 14;

  const compliantRows = useMemo(() => {
    const rows = getCompliantLpgRows({
      bidders,
      requirement,
      requirementIndex,
      parcelId,
      deliveryDetails,
    });
    return improvementOnly ? filterImprovementOfferRows(rows) : rows;
  }, [
    bidders,
    requirement,
    requirementIndex,
    parcelId,
    deliveryDetails,
    improvementOnly,
  ]);

  const parcelSizes = useMemo(
    () =>
      collectParcelSizeLabels(
        parcel,
        compliantRows.map(row => row.detailRow)
      ),
    [parcel, compliantRows]
  );

  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedRowKeys, setSelectedRowKeys] = useState(() => new Set());

  const filteredRows = useMemo(() => {
    if (statusFilter === 'all') return compliantRows;
    if (statusFilter === 'counter') {
      return compliantRows.filter(row => row.kind === 'counter');
    }
    return compliantRows.filter(row => row.kind === 'offer');
  }, [compliantRows, statusFilter]);

  useEffect(() => {
    if (improvementOnly || !selectionClearToken) return;
    setSelectedRowKeys(new Set());
  }, [selectionClearToken, improvementOnly]);

  useEffect(() => {
    if (improvementOnly) return;
    const validKeys = new Set(
      compliantRows.map(
        row =>
          row.selectionKey ??
          `${row.bidder?.id ?? row.bidder?.idOffer}-${row.kind}`
      )
    );
    setSelectedRowKeys(prev => {
      const next = new Set([...prev].filter(key => validKeys.has(key)));
      return next.size === prev.size ? prev : next;
    });
  }, [compliantRows, improvementOnly]);

  useEffect(() => {
    if (improvementOnly) return;
    const improvementKeys = compliantRows
      .filter(row => isOfferRowImprovement(row))
      .map(
        row =>
          row.selectionKey ??
          `${row.bidder?.id ?? row.bidder?.idOffer}-${row.kind}`
      )
      .filter(Boolean);
    if (!improvementKeys.length) return;

    setSelectedRowKeys(prev => {
      const next = new Set(prev);
      let changed = false;
      improvementKeys.forEach(key => {
        if (!next.has(key)) {
          next.add(key);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [compliantRows, improvementOnly]);

  useEffect(() => {
    if (improvementOnly || !selectionKey || !onRequirementSelectionChange)
      return;
    onRequirementSelectionChange(
      selectionKey,
      collectImprovementSelections({
        compliantRows,
        selectedKeys: [...selectedRowKeys],
        parcelId,
        requirementIndex,
        requirement,
        keyField: 'selectionKey',
      })
    );
  }, [
    selectionKey,
    selectedRowKeys,
    compliantRows,
    parcelId,
    requirementIndex,
    requirement,
    onRequirementSelectionChange,
    improvementOnly,
  ]);

  const selectableKeys = useMemo(
    () => [
      ...new Set(
        filteredRows
          .filter(row => !isOfferRowImprovement(row))
          .map(
            row =>
              row.selectionKey ??
              `${row.bidder?.id ?? row.bidder?.idOffer}-${row.kind}`
          )
      ),
    ],
    [filteredRows]
  );

  const allSelected =
    selectableKeys.length > 0 &&
    selectableKeys.every(key => selectedRowKeys.has(key));
  const someSelected = selectableKeys.some(key => selectedRowKeys.has(key));

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someSelected && !allSelected;
    }
  }, [someSelected, allSelected]);

  const handleSelectAll = checked => {
    setSelectedRowKeys(prev => {
      const next = new Set(prev);
      selectableKeys.forEach(key => {
        if (checked) next.add(key);
        else next.delete(key);
      });
      return next;
    });
  };

  const handleSelectRow = (key, checked) => {
    if (!key) return;
    const row = compliantRows.find(
      entry =>
        (entry.selectionKey ??
          `${entry.bidder?.id ?? entry.bidder?.idOffer}-${entry.kind}`) === key
    );
    if (row && isOfferRowImprovement(row)) return;
    setSelectedRowKeys(prev => {
      const next = new Set(prev);
      if (checked) next.add(key);
      else next.delete(key);
      return next;
    });
  };

  if (!hasData && !isLoadingBidders && !isLoadingDeliveryDetails) {
    return null;
  }

  return (
    <Card className='border shadow-none mb-3'>
      <CardHeader
        className='bg-light border-bottom p-3 d-flex justify-content-between align-items-center cursor-pointer'
        onClick={() => setIsExpanded(prev => !prev)}
        role='button'
        aria-expanded={isExpanded}
        aria-controls={`requirement-body-lpg-${requirementIndex}`}
      >
        <div>
          <h6 className='mb-0 text-dark fw-bold fs-13'>
            Requirement {requirementIndex + 1}
          </h6>
          <span className='text-muted fs-12'>{incotermRouteLabel}</span>
        </div>
        <i
          className={`fs-18 ${isExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}
        ></i>
      </CardHeader>

      <Collapse isOpen={isExpanded}>
        <CardBody
          id={`requirement-body-lpg-${requirementIndex}`}
          className='p-0'
        >
          <div className='p-3 border-bottom'>
            <Row className='g-3'>
              <Col md={3}>
                <div className='text-muted fw-semibold fs-12 mb-2'>
                  Incoterm - Route
                </div>
                <div className='text-dark fs-13 mt-4'>
                  {[requirement.incoterm].filter(Boolean).join(' - ') || '-'}
                </div>
              </Col>
              <Col md={3}>
                <div className='text-muted fw-semibold fs-12 mb-2'>
                  Operational Tolerances
                </div>
                <div className='text-dark fs-13 mt-4'>
                  {operationalTolerance != null && operationalTolerance !== ''
                    ? `${operationalTolerance}%`
                    : '-'}
                </div>
              </Col>
              <PortSummary
                label='Port 1'
                port={requirement.port1}
                parcelSizes={parcelSizes}
                dateRange={formatTimelineRange(timeline)}
              />
              <PortSummary
                label='Port 2'
                port={requirement.port2}
                showParcelSize={false}
              />
              <PortSummary
                label='Port 3'
                port={requirement.port3}
                showParcelSize={false}
              />
            </Row>
          </div>

          <div className='table-responsive m-3 border rounded'>
            <Table className='table-borderless align-middle mb-0 custom-table-spacing'>
              <thead className='bg-light text-muted fs-12 border-bottom'>
                <tr>
                  {!improvementOnly && (
                    <th
                      scope='col'
                      style={{ width: '44px' }}
                      className='text-center'
                    >
                      <Input
                        innerRef={selectAllRef}
                        type='checkbox'
                        className='form-check-input'
                        checked={allSelected}
                        disabled={
                          isLoadingBidders ||
                          isLoadingDeliveryDetails ||
                          !selectableKeys.length
                        }
                        onChange={e => handleSelectAll(e.target.checked)}
                        aria-label='Pilih semua offer'
                      />
                    </th>
                  )}
                  {!improvementOnly && (
                    <th scope='col' style={{ minWidth: '130px' }}>
                      Status Improvement
                    </th>
                  )}
                  <th scope='col'>ID Offer</th>
                  <th scope='col' style={{ minWidth: '130px' }}>
                    <div className='d-flex flex-column gap-1'>
                      <span>Status</span>
                      <Input
                        type='select'
                        bsSize='sm'
                        className='form-select form-select-sm py-0'
                        style={{ minHeight: '28px' }}
                        value={statusFilter}
                        onChange={e => setStatusFilter(e.target.value)}
                        onClick={e => e.stopPropagation()}
                        aria-label='Filter status'
                      >
                        <option value='all'>All</option>
                        <option value='compliant'>Compliant</option>
                        <option value='counter'>Counter</option>
                      </Input>
                    </div>
                  </th>
                  <th scope='col'>Incoterm - Route</th>
                  <th scope='col'>Operational Tolerances</th>
                  <th scope='col' style={{ minWidth: '180px' }}>
                    Port 1
                  </th>
                  <th scope='col'>Port 2</th>
                  <th scope='col'>Port 3</th>
                  {requiresCargoRatio && <th scope='col'>Cargo Ratio</th>}
                  <th scope='col'>Parcel Size</th>
                  <th scope='col'>Alpha (USD/MT)</th>
                  {!improvementOnly && (
                    <>
                      <th scope='col' style={{ minWidth: '120px' }}>
                        HPS Alpha
                      </th>
                      <th scope='col' style={{ minWidth: '170px' }}>
                        Matching HPS
                      </th>
                    </>
                  )}
                  {improvementOnly ? (
                    usesDyedUndyed ? (
                      <>
                        <th scope='col'>Alpha Dyed (Improvement)</th>
                        <th scope='col'>Alpha Undyed (Improvement)</th>
                      </>
                    ) : (
                      <th scope='col'>Alpha (Improvement)</th>
                    )
                  ) : (
                    <th scope='col'>Action</th>
                  )}
                </tr>
              </thead>
              <tbody className='fs-13'>
                <OfferDetailRows
                  compliantRows={filteredRows}
                  isLoading={isLoadingBidders || isLoadingDeliveryDetails}
                  selectedRowKeys={selectedRowKeys}
                  onSelectRow={handleSelectRow}
                  requiresCargoRatio={requiresCargoRatio}
                  operationalTolerance={operationalTolerance}
                  parcelSizes={parcelSizes}
                  onViewDetail={onViewDetail}
                  improvementOnly={improvementOnly}
                  usesDyedUndyed={usesDyedUndyed}
                  colSpan={colSpan}
                  alphaImprovementEditable={alphaImprovementEditable}
                  onAlphaImprovementChange={onAlphaImprovementChange}
                  hpsAvailable={isHpsAvailable(procurement)}
                />
              </tbody>
            </Table>
          </div>
        </CardBody>
      </Collapse>
    </Card>
  );
};

const ParcelOfferCard = ({
  parcel,
  procurement,
  bidders,
  isLoadingBidders,
  deliveryDetails,
  isLoadingDeliveryDetails,
  requiresCargoRatio,
  onViewDetail,
  improvementOnly = false,
  onRequirementSelectionChange,
  selectionClearToken = 0,
  alphaImprovementEditable = false,
  onAlphaImprovementChange,
}) => {
  const requirements = useMemo(
    () => buildLpgRequirementsFromParcel(parcel),
    [parcel]
  );
  const parcelId = parcel?.id ?? parcel?.quantityDeliveryTermId ?? null;
  const parcelDeliveryDetails = useMemo(
    () =>
      parcelId
        ? deliveryDetails.filter(
            row => String(row.quantityDeliveryTermId) === String(parcelId)
          )
        : deliveryDetails,
    [deliveryDetails, parcelId]
  );
  const operationalTolerance = procurement?.operationalTolerance;
  const headerTolerance = operationalTolerance;

  return (
    <Card className='border shadow-none mb-4'>
      <CardHeader className='bg-light border-bottom p-3'>
        <h6 className='mb-0 text-dark fw-bold fs-14'>
          Delivery Unit : {displayValue(parcel?.deliveryUnit, '-')}
        </h6>
      </CardHeader>

      <CardBody className='p-4'>
        <Row className='mb-4'>
          <Col md={4} className='border-end'>
            <div className='text-dark fw-semibold fs-13 mb-1'>Product Name</div>
            <div className='text-muted fs-13'>
              {getProductLabel(procurement)}
            </div>
          </Col>
          <Col md={4} className='border-end'>
            <div className='text-dark fw-semibold fs-13 mb-1'>Quantity</div>
            <div className='text-muted fs-13'>{getQuantityLabel(parcel)}</div>
          </Col>
          <Col md={4}>
            <div className='text-dark fw-semibold fs-13 mb-1'>
              Operation Tolerance
            </div>
            <div className='text-muted fs-13'>
              {headerTolerance != null && headerTolerance !== ''
                ? `${headerTolerance}%`
                : '-'}
            </div>
          </Col>
        </Row>

        {requirements.map((requirement, index) => (
          <RequirementCard
            key={`${parcel?.id || 'parcel'}-req-${index}`}
            selectionKey={`${parcelId || 'parcel'}-req-${index}`}
            requirement={requirement}
            requirementIndex={index}
            parcel={parcel}
            bidders={bidders}
            isLoadingBidders={isLoadingBidders}
            parcelId={parcelId}
            deliveryDetails={parcelDeliveryDetails}
            isLoadingDeliveryDetails={isLoadingDeliveryDetails}
            procurement={procurement}
            requiresCargoRatio={requiresCargoRatio}
            onViewDetail={onViewDetail}
            improvementOnly={improvementOnly}
            onRequirementSelectionChange={onRequirementSelectionChange}
            selectionClearToken={selectionClearToken}
            alphaImprovementEditable={alphaImprovementEditable}
            onAlphaImprovementChange={onAlphaImprovementChange}
          />
        ))}
      </CardBody>
    </Card>
  );
};

const OfferTableLpg = ({
  procurementId,
  procurement,
  parcels = [],
  isLoadingParcels = false,
  onSelectedOffersChange,
  selectionClearToken = 0,
  viewMode = 'offer',
  alphaImprovementEditable = false,
  onAlphaImprovementChange,
  onImprovementMeta,
  isActive = true,
}) => {
  const improvementOnly = viewMode === 'improvement';
  const { items: bidders, isLoading: isLoadingBidders } = useSubmittedBidder(
    procurementId,
    { isActive, refreshToken: selectionClearToken }
  );

  const requiresCargoRatio = useMemo(
    () =>
      requiresLpgCargoRatio({
        productNameId:
          procurement?.productType?.product?.id ??
          procurement?.productType?.Product?.Id,
        productTypeId:
          procurement?.productType?.id ?? procurement?.productType?.Id,
      }),
    [procurement]
  );

  const baseDeliveryDetails = useMemo(
    () => flattenSubmittedBidderDeliveryDetails(bidders),
    [bidders]
  );

  const [deliveryDetails, setDeliveryDetails] = useState([]);
  const [isEnriching, setIsEnriching] = useState(false);
  const [hasEnrichmentSettled, setHasEnrichmentSettled] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectionByRequirement, setSelectionByRequirement] = useState({});

  const handleRequirementSelectionChange = useCallback(
    (selectionKey, entries) => {
      setSelectionByRequirement(prev => {
        const prevEntries = prev[selectionKey] || [];
        if (
          prevEntries.length === entries.length &&
          prevEntries.every(
            (entry, index) =>
              entry.quantityIncotermId === entries[index]?.quantityIncotermId &&
              entry.kind === entries[index]?.kind
          )
        ) {
          return prev;
        }
        return { ...prev, [selectionKey]: entries };
      });
    },
    []
  );

  useEffect(() => {
    if (improvementOnly || !onSelectedOffersChange) return;
    onSelectedOffersChange?.(
      Object.values(selectionByRequirement).flatMap(entries => entries || [])
    );
  }, [selectionByRequirement, onSelectedOffersChange, improvementOnly]);

  useEffect(() => {
    if (improvementOnly || !selectionClearToken) return;
    setSelectionByRequirement({});
  }, [selectionClearToken, improvementOnly]);

  useEffect(() => {
    let cancelled = false;

    const enrichDetails = async () => {
      setHasEnrichmentSettled(false);

      if (!baseDeliveryDetails.length) {
        if (!cancelled) {
          setDeliveryDetails([]);
          setIsEnriching(false);
          setHasEnrichmentSettled(true);
        }
        return;
      }

      const token = getAuthToken();
      if (!token || !procurementId) {
        if (!cancelled) {
          setDeliveryDetails(baseDeliveryDetails);
          setIsEnriching(false);
          setHasEnrichmentSettled(true);
        }
        return;
      }

      setIsEnriching(true);

      try {
        const byTermId = new Map();
        baseDeliveryDetails.forEach(row => {
          const termId = row.quantityDeliveryTermId ?? 'default';
          if (!byTermId.has(termId)) byTermId.set(termId, []);
          byTermId.get(termId).push(row);
        });

        const enriched = [];
        for (const [termId, items] of byTermId.entries()) {
          let nextItems = items;

          const hasCargoRatio = nextItems.some(
            item => item.cargoRatio || item.cargoRatioLabel
          );

          if (termId !== 'default' && requiresCargoRatio && !hasCargoRatio) {
            try {
              nextItems = await enrichLpgDeliveryItemsWithCargoRatio({
                procurementId,
                quantityDeliveryTermId: termId,
                items: nextItems,
                skipCargoRatio: false,
              });
            } catch (error) {
              // keep base items when cargo-ratio enrichment fails
            }
          } else if (termId !== 'default' && !requiresCargoRatio) {
            try {
              nextItems = await enrichLpgDeliveryItemsWithCargoRatio({
                procurementId,
                quantityDeliveryTermId: termId,
                items: nextItems,
                skipCargoRatio: true,
              });
            } catch (error) {
              // keep base items when parcel-size enrichment fails
            }
          }

          if (termId !== 'default') {
            try {
              const detailResponse = await fetch(
                `${api.API_URL}/procurement/get-delivery-detail?PageNumber=1&PageSize=500&procurementId=${encodeURIComponent(procurementId)}&quantityDeliveryTermId=${encodeURIComponent(termId)}`,
                {
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                  },
                  credentials: 'same-origin',
                }
              );
              if (detailResponse.ok) {
                const detailResult = await detailResponse.json();
                const bidderMap = Object.fromEntries(
                  (bidders || []).map(bidder => [String(bidder.id), bidder])
                );
                const detailRows = flattenEvaluationDeliveryDetail(
                  detailResult,
                  bidderMap,
                  termId
                );
                nextItems = mergeDeliveryDetailPricingIntoRows(
                  nextItems,
                  detailRows
                );
              }
            } catch (error) {
              // keep submitted-bidder rows when delivery-detail merge fails
            }
            try {
              nextItems = await enrichDeliveryItemsWithFobLocations({
                quantityDeliveryTermId: termId,
                token,
                items: nextItems,
              });
            } catch (error) {
              // keep items when FOB enrichment fails
            }
            try {
              nextItems = await enrichDeliveryItemsWithCounterOffers({
                quantityDeliveryTermId: termId,
                token,
                items: nextItems,
                requireIncotermCounter: true,
              });
            } catch (error) {
              // keep items when counter enrichment fails
            }
          }

          enriched.push(...nextItems);
        }

        if (!cancelled) setDeliveryDetails(enriched);
      } finally {
        if (!cancelled) {
          setIsEnriching(false);
          setHasEnrichmentSettled(true);
        }
      }
    };

    enrichDetails();

    return () => {
      cancelled = true;
    };
  }, [
    baseDeliveryDetails,
    procurementId,
    requiresCargoRatio,
    selectionClearToken,
    bidders,
  ]);

  useEffect(() => {
    if (!improvementOnly || !onImprovementMeta) return;
    if (!hasEnrichmentSettled || isEnriching) return;
    onImprovementMeta({ deliveryDetails, ready: true });
  }, [
    deliveryDetails,
    improvementOnly,
    onImprovementMeta,
    hasEnrichmentSettled,
    isEnriching,
  ]);

  const handleViewOfferDetail = (bidder, detailRow) => {
    if (!bidder?.id) return;
    setSelectedOffer({ bidder, detailRow });
    setIsDetailModalOpen(true);
  };

  const toggleDetailModal = () => {
    setIsDetailModalOpen(prev => !prev);
    if (isDetailModalOpen) {
      setSelectedOffer(null);
    }
  };

  const hasImprovementData = useMemo(() => {
    if (!improvementOnly) return true;
    const parcelItems = parcels.length
      ? parcels
      : [{ id: 'default', deliveryUnit: '-' }];
    return parcelItems.some(parcel => {
      const parcelId = parcel?.id ?? parcel?.quantityDeliveryTermId ?? null;
      const parcelDeliveryDetails = parcelId
        ? deliveryDetails.filter(
            row => String(row.quantityDeliveryTermId) === String(parcelId)
          )
        : deliveryDetails;
      return buildLpgRequirementsFromParcel(parcel).some((requirement, index) =>
        requirementHasOfferData({
          requirement,
          requirementIndex: index,
          parcelId,
          deliveryDetails: parcelDeliveryDetails,
          bidders,
          improvementOnly: true,
        })
      );
    });
  }, [improvementOnly, parcels, deliveryDetails, bidders]);

  if (isLoadingParcels || isLoadingBidders) {
    return (
      <div className='text-center py-4'>
        <Spinner size='sm' color='primary' />
        <p className='text-muted mt-2 mb-0 fs-13'>Loading offer data...</p>
      </div>
    );
  }

  if (improvementOnly && (!hasEnrichmentSettled || isEnriching)) {
    return (
      <div className='text-center py-4'>
        <Spinner size='sm' color='primary' />
        <p className='text-muted mt-2 mb-0 fs-13'>
          Loading improvement data...
        </p>
      </div>
    );
  }

  if (!bidders.length) {
    return (
      <div className='text-center text-muted py-4 fs-13'>
        {improvementOnly
          ? 'Belum ada data improvement.'
          : 'Belum ada penawaran yang disubmit.'}
      </div>
    );
  }

  if (improvementOnly && !hasImprovementData) {
    return (
      <div className='text-center text-muted py-4 fs-13'>
        Belum ada data improvement.
      </div>
    );
  }

  const parcelItems = parcels.length
    ? parcels
    : [{ id: 'default', deliveryUnit: '-' }];

  return (
    <>
      {parcelItems.map(parcel => (
        <ParcelOfferCard
          key={parcel.id || parcel.deliveryUnit}
          parcel={parcel}
          procurement={procurement}
          bidders={bidders}
          isLoadingBidders={isLoadingBidders}
          deliveryDetails={deliveryDetails}
          isLoadingDeliveryDetails={isEnriching}
          requiresCargoRatio={requiresCargoRatio}
          onViewDetail={handleViewOfferDetail}
          improvementOnly={improvementOnly}
          onRequirementSelectionChange={
            improvementOnly ? undefined : handleRequirementSelectionChange
          }
          selectionClearToken={improvementOnly ? 0 : selectionClearToken}
          alphaImprovementEditable={alphaImprovementEditable}
          onAlphaImprovementChange={onAlphaImprovementChange}
        />
      ))}
      {!improvementOnly && (
        <ModalOffer
          isOpen={isDetailModalOpen}
          toggle={toggleDetailModal}
          bidder={selectedOffer?.bidder}
          procurement={procurement}
        />
      )}
    </>
  );
};

export default OfferTableLpg;
